import { EntityStore } from "@use/entity-store";

import { AuthenticatedUserModel, authorizeUser } from "@courses-utils/gateway-auth";

import { getConfig, PM2PortalConfig } from "@courses-pm2/api/src/ts/methods/getConfig";

/**
 * Contains a cache of authorization states for users across different portals.
 */
const authorizationCache = new EntityStore<boolean>({ ttl: 3600 });

/**
 * Contains a cache of access keys used to authorize users againns
 */
const portalConfigCache = new EntityStore<PM2PortalConfig>({ ttl: 3600 });

/**
 * Helper that'll determine if the user has been authorized to access content
 * from within the desired portal.
 */
export async function authorizePortalAccess(user: AuthenticatedUserModel, portalId: string): Promise<boolean> {
    const authCacheId = `${user.id}-${portalId}`;

    // If we've already cached authorization status for the user on the given
    // portal, then return the result as is
    if (authorizationCache.has(authCacheId)) {
        return <boolean>authorizationCache.get(authCacheId);
    }

    // Otherwise the first thing to do, is to fetch configuration of the portal,
    // so we can determine how to run authentication
    if (!portalConfigCache.has(portalId)) {
        portalConfigCache.set(portalId, await getConfig(portalId));
    }

    // tslint:disable-next-line no-non-null-assertion
    const portalConfig = portalConfigCache.get(portalId)!;

    // if content is freely available after having signed in, then the user is
    // automatically considered authorized
    if (portalConfig.loginFreeAccess) {
        authorizationCache.set(authCacheId, true);

        return true;
    }

    // ... otherwise determine if the user has been granted a valid license to
    // access the portal using the list of defined accessKeys
    const accessKeys = (
        portalConfig.accessKeys && portalConfig.accessKeys.length > 0
            ? portalConfig.accessKeys
            : [portalConfig.uniqueId]
    );

    const isAuthorized = await authorizeUser(user, accessKeys);

    // cache result for future reference
    authorizationCache.set(authCacheId, isAuthorized);

    return isAuthorized;
}
